import dayjs from 'dayjs';
import { ReceiptEventPackageItemProps, ReceiptEventPackageProps } from '../../../datastore/ssccs';
import { ManifestProps } from '../../../datastore/tripSheets';
import { ManifestPackageStatus } from '../../../common/status';
import { variousAsn } from '../../../common/constants';

export type AsnLineReceiveQuantityOverrides = Record<string, number>;

export function getAsnLineReceiveQuantityKey(
  asn: string,
  line: Pick<ReceiptEventPackageItemProps, 'orderLineId' | 'partId' | 'sourcePartId'>,
  sscc?: string | number | null
): string {
  return `${String(asn || '')
    .trim()
    .toLowerCase()}::${String(line.orderLineId || '').trim()}::${String(line.partId || '').trim()}::${String(
    line.sourcePartId || ''
  )
    .trim()
    .toLowerCase()}::${String(sscc || '')
    .trim()
    .toLowerCase()}`;
}

type ReceiptEventPackageItemWithOverrideKey = ReceiptEventPackageItemProps & {
  __receiveOverrideKey?: string;
};

export function mapToReceiptedItems(
  manifest: ManifestProps,
  receiveQuantityOverrides: AsnLineReceiveQuantityOverrides = {},
  options: { includeZeroQuantityPackages?: boolean } = {}
): Partial<ReceiptEventPackageProps>[] {
  const asnMap = new Map<string, Partial<ReceiptEventPackageProps>>();
  const overrideKeys = new Set(Object.keys(receiveQuantityOverrides));
  const includeZeroQuantityPackages = Boolean(options.includeZeroQuantityPackages);

  manifest?.ssccs?.forEach((sscc) => {
    sscc.packages?.forEach((pkg) => {
      if (
        pkg.receivedStatus !== ManifestPackageStatus.qm_pending &&
        pkg.receivedStatus !== ManifestPackageStatus.qm &&
        pkg.asn !== variousAsn &&
        (includeZeroQuantityPackages || Number(pkg.quantity ?? 0) > 0) &&
        (pkg.inventoryLines ?? []).length > 0
      ) {
        const items: ReceiptEventPackageItemWithOverrideKey[] = pkg.inventoryLines!.map((line) => {
          const scopedOverrideKey = getAsnLineReceiveQuantityKey(pkg.asn, line, sscc.ssccId);
          const legacyOverrideKey = getAsnLineReceiveQuantityKey(pkg.asn, line);
          const matchedOverrideKey = overrideKeys.has(scopedOverrideKey)
            ? scopedOverrideKey
            : overrideKeys.has(legacyOverrideKey)
              ? legacyOverrideKey
              : undefined;

          return {
            orderLineId: line.orderLineId,
            partId: line.partId,
            sourcePartId: line.sourcePartId ?? '',
            quantityReceived:
              (matchedOverrideKey ? receiveQuantityOverrides[matchedOverrideKey] : undefined) ??
              line.quantityReceived ??
              0,
            purchaseType: line.purchaseType,
            receipt: dayjs(),
            receivingWarehouseId: line.receivingWarehouseId ?? null,
            receivingWarehouseDefaultLocationId: line.receivingWarehouseDefaultLocationId ?? null,
            inTransitWarehouseId: line.inTransitWarehouseId ?? null,
            inTransitWarehouseLocationId: line.inTransitWarehouseLocationId ?? null,
            __receiveOverrideKey: matchedOverrideKey
          };
        });

        if (!asnMap.has(pkg.asn)) {
          asnMap.set(pkg.asn, {
            id: pkg.id,
            asn: pkg.asn,
            po: pkg.poNumber ?? null,
            quantity: pkg.quantity ?? 0,
            items: items
          });
        } else {
          const existingEntry = asnMap.get(pkg.asn)!;
          existingEntry.quantity = Number(existingEntry.quantity || 0) + Number(pkg.quantity || 0);
          existingEntry.items = [...(existingEntry.items ?? []), ...items];
        }
      }
    });
  });

  // Deduplicate items within each ASN
  for (const entry of asnMap.values()) {
    if (entry.items) {
      const itemsByKey = new Map<string, ReceiptEventPackageItemWithOverrideKey>();
      for (const item of entry.items) {
        const key = `${item.orderLineId}-${item.partId}-${item.sourcePartId}`;
        const typedItem = item as ReceiptEventPackageItemWithOverrideKey;
        const existingItem = itemsByKey.get(key);
        if (!existingItem) {
          itemsByKey.set(key, typedItem);
          continue;
        }

        if (!typedItem.__receiveOverrideKey || typedItem.__receiveOverrideKey !== existingItem.__receiveOverrideKey) {
          existingItem.quantityReceived =
            Number(existingItem.quantityReceived || 0) + Number(typedItem.quantityReceived || 0);
        }
      }
      entry.items = Array.from(itemsByKey.values()).map(({ __receiveOverrideKey, ...item }) => item);
    }
  }

  return Array.from(asnMap.values()).filter((entry) => (entry.items?.length ?? 0) > 0);
}
